import { z } from 'zod';

import {
  AI_CALL_STATUSES,
  AI_FEATURES,
  AI_PROVIDERS,
  DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD,
  DEFAULT_DAILY_USD_CAP,
  DEFAULT_MONTHLY_USD_CAP,
  INBOUND_EMAIL_STATUSES,
  INBOUND_EMAIL_SUGGESTED_ACTIONS,
  SUPER_ADMIN_DAILY_CEILING,
  SUPER_ADMIN_MONTHLY_CEILING,
  VOICE_MEMO_MAX_SECONDS,
} from './ai-constants.js';
import { COMPLIANCE_LOG_OUTCOMES } from './compliance-constants.js';
import { WORK_ORDER_CATEGORIES, WORK_ORDER_SEVERITIES } from './work-order-constants.js';

/**
 * Phase 8 — AI layer Zod contracts.
 *
 * The model-output schemas (`emailParseToolResultSchema`,
 * `woClassificationToolResultSchema`, etc.) are the authoritative parsers
 * the runner uses to coerce Anthropic tool responses. Anything the model
 * returns that doesn't fit gets logged as `error` and the caller falls
 * through to the non-AI path — never crash the user-visible flow because
 * the model returned shape drift.
 */

// ---------- Org-admin AI quota mutations ----------

/**
 * Org admin's edit surface for `ai_quotas`. The API additionally enforces
 * the super-admin ceiling at the same shape; super-admin endpoints reuse
 * this schema with a wider ceiling.
 */
export const aiQuotaUpdateInput = z.object({
  dailyUsdCap: z
    .number()
    .min(0)
    .max(SUPER_ADMIN_DAILY_CEILING)
    .optional(),
  monthlyUsdCap: z
    .number()
    .min(0)
    .max(SUPER_ADMIN_MONTHLY_CEILING)
    .optional(),
  featuresEnabled: z
    .object({
      email_parse: z.boolean().optional(),
      compliance_doc_parse: z.boolean().optional(),
      wo_classify: z.boolean().optional(),
      violation_summarize: z.boolean().optional(),
      voice_to_wo: z.boolean().optional(),
    })
    .optional(),
});
export type AiQuotaUpdateInput = z.infer<typeof aiQuotaUpdateInput>;

export const aiQuotaPauseInput = z.object({
  reason: z.string().trim().max(280).optional(),
});
export type AiQuotaPauseInput = z.infer<typeof aiQuotaPauseInput>;

// ---------- Tool-output schemas (what Claude returns) ----------

/**
 * Email-parse tool response. `workOrderId` is null when the model
 * couldn't pick a candidate; `confidence` is always present so the
 * runner can branch on it deterministically.
 */
export const emailParseToolResultSchema = z.object({
  workOrderId: z.string().uuid().nullable(),
  confidence: z.number().min(0).max(1),
  suggestedAction: z.enum(INBOUND_EMAIL_SUGGESTED_ACTIONS),
  summary: z.string().trim().min(1).max(1000),
  reasoning: z.string().trim().max(800).optional(),
});
export type EmailParseToolResult = z.infer<typeof emailParseToolResultSchema>;

export const woClassificationToolResultSchema = z.object({
  category: z.enum(WORK_ORDER_CATEGORIES),
  severity: z.enum(WORK_ORDER_SEVERITIES),
  confidence: z.number().min(0).max(1),
  rationale: z.string().trim().min(1).max(280),
});
export type WoClassificationToolResult = z.infer<typeof woClassificationToolResultSchema>;

export const violationSummarizeToolResultSchema = z.object({
  summary: z.string().trim().min(1).max(400),
});
export type ViolationSummarizeToolResult = z.infer<typeof violationSummarizeToolResultSchema>;

/**
 * Voice → WO structuring. The model returns canonical fields in the same
 * vocabulary the WO create form uses; `confidence` is the model's
 * self-rated confidence in the title/description pairing.
 */
export const voiceStructureToolResultSchema = z.object({
  title: z.string().trim().min(1).max(200),
  // The prompt instructs the model to always emit a description (empty
  // string when nothing extra to say). Keeping input/output identical
  // here lets the schema be plugged into Anthropic's tool-use API
  // without input/output type drift.
  description: z.string().trim().max(2000),
  suggestedCategory: z.enum(WORK_ORDER_CATEGORIES),
  suggestedSeverity: z.enum(WORK_ORDER_SEVERITIES),
  confidence: z.number().min(0).max(1),
  /** Matched building from the org catalog, or null when unsure. */
  buildingId: z.string().uuid().nullable().optional(),
  /** Matched unit from the catalog, or null when not mentioned / unsure. */
  unitId: z.string().uuid().nullable().optional(),
  /** Confidence in the building/unit match specifically. */
  locationConfidence: z.number().min(0).max(1).optional(),
});
export type VoiceStructureToolResult = z.infer<typeof voiceStructureToolResultSchema>;

export const complianceDocParseToolResultSchema = z.object({
  deviceTypeSlug: z.string().trim().min(1).max(80).nullable(),
  buildingId: z.string().uuid().nullable(),
  deviceId: z.string().uuid().nullable(),
  performedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  outcome: z.enum(COMPLIANCE_LOG_OUTCOMES).nullable(),
  make: z.string().trim().max(120).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  serialNumber: z.string().trim().max(120).nullable().optional(),
  suggestedNextDueOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  summary: z.string().trim().min(1).max(800),
  confidence: z.number().min(0).max(1),
  reasoning: z.string().trim().max(800).optional(),
});
export type ComplianceDocParseToolResult = z.infer<typeof complianceDocParseToolResultSchema>;

export const createComplianceIngestInput = z.object({
  uploadId: z.string().uuid(),
  filename: z.string().trim().min(1).max(255).optional(),
});
export type CreateComplianceIngestInput = z.infer<typeof createComplianceIngestInput>;

export const confirmComplianceIngestInput = z.object({
  deviceId: z.string().uuid(),
  performedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  outcome: z.enum(COMPLIANCE_LOG_OUTCOMES).default('passed'),
  notes: z.string().trim().max(8_000).nullish().transform((v) => v ?? null),
  nextDueOverride: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .transform((v) => v ?? null),
});
export type ConfirmComplianceIngestInput = z.infer<typeof confirmComplianceIngestInput>;

// ---------- Frontend request bodies ----------

export const voiceTranscribeRequest = z.object({
  uploadId: z.string().uuid(),
  /** ISO 639-1 hint passed to Whisper. Optional — Whisper auto-detects. */
  language: z.string().min(2).max(8).optional(),
});
export type VoiceTranscribeRequest = z.infer<typeof voiceTranscribeRequest>;

export const voiceStructureRequest = z.object({
  transcript: z.string().trim().min(1).max(8000),
  buildingId: z.string().uuid().optional(),
  unitId: z.string().uuid().optional(),
});
export type VoiceStructureRequest = z.infer<typeof voiceStructureRequest>;

// ---------- Review-queue mutations ----------

export const reviewQueueAcceptInput = z.object({
  workOrderId: z.string().uuid(),
});
export type ReviewQueueAcceptInput = z.infer<typeof reviewQueueAcceptInput>;

export const reviewQueueRerouteInput = z.object({
  workOrderId: z.string().uuid(),
});
export type ReviewQueueRerouteInput = z.infer<typeof reviewQueueRerouteInput>;

export const reviewQueueRejectInput = z.object({
  reason: z.string().trim().max(280).optional(),
});
export type ReviewQueueRejectInput = z.infer<typeof reviewQueueRejectInput>;

// ---------- Classification accept/dismiss ----------

export const aiClassificationAcceptInput = z
  .object({
    /** When omitted the suggestion is applied verbatim. Pass to override. */
    category: z.enum(WORK_ORDER_CATEGORIES).optional(),
    severity: z.enum(WORK_ORDER_SEVERITIES).optional(),
  })
  .default({});
export type AiClassificationAcceptInput = z.infer<typeof aiClassificationAcceptInput>;

// ---------- Voice memo upload guard ----------

export const voiceMemoMaxSeconds = VOICE_MEMO_MAX_SECONDS;

// ---------- Re-export the literal lists so consumers don't need both files ----------

export {
  AI_FEATURES,
  AI_PROVIDERS,
  AI_CALL_STATUSES,
  INBOUND_EMAIL_STATUSES,
  INBOUND_EMAIL_SUGGESTED_ACTIONS,
  DEFAULT_DAILY_USD_CAP,
  DEFAULT_MONTHLY_USD_CAP,
  DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD,
};
